import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ActiveSyndicate {
  id: string;
  horse_id: string;
  name: string;
  description: string | null;
  photo_url: string | null;
  thumbnail_url: string | null; // Added for optimized loading
  race_type: 'flat' | 'steeplechase' | null;
  video_profile_url: string | null;
  pedigree: {
    sire?: string;
    dam?: string;
    sire_of_sire?: string;
    dam_of_sire?: string;
    sire_of_dam?: string;
    dam_of_dam?: string;
  } | null;
  contract_start_date: string;
  contract_end_date: string;
  target_credits: number;
  current_credits: number;
  owner_credits: number;
  sponsor_credits: number;
  unactivated_gift_credits: number; // Kredity z neaktivovaných dárkových kódů
  status: string;
  individual_horses: {
    name: string;
    color: string | null;
    sex: string | null;
    birth_date: string | null;
  } | null;
  syndicate_media?: Array<{
    thumbnail_url: string | null;
    original_url: string;
    media_type: 'photo' | 'video';
  }>;
}

export const useActiveSyndicates = () => {
  return useQuery({
    queryKey: ["active-syndicates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("syndicates")
        .select(`
          *,
          individual_horses (
            name,
            color,
            sex,
            birth_date
          ),
          syndicate_media (
            thumbnail_url,
            original_url,
            media_type
          )
        `)
        .eq("status", "active")
        .order("display_order", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch unactivated gift credits for each syndicate (using safe public view)
      const syndicateIds = data?.map(s => s.id) || [];
      const { data: giftCodes, error: giftError } = await supabase
        .from("syndicate_gift_codes_public" as any)
        .select("syndicate_id, credits")
        .in("syndicate_id", syndicateIds)
        .eq("is_activated", false);

      if (giftError) throw giftError;

      // Aggregate gift credits by syndicate_id
      const giftCreditsMap = ((giftCodes || []) as any[]).reduce((acc: Record<string, number>, code: any) => {
        acc[code.syndicate_id] = (acc[code.syndicate_id] || 0) + code.credits;
        return acc;
      }, {} as Record<string, number>);
      
      // Set thumbnail_url based on the main photo_url if available
      const syndicatesWithThumbnails = data?.map(syndicate => {
        let thumbnailUrl = null;
        
        // If there's a main photo_url set, find its thumbnail in the media gallery
        if (syndicate.photo_url && syndicate.syndicate_media) {
          const mainMedia = syndicate.syndicate_media.find(
            (media: any) => media.original_url === syndicate.photo_url
          );
          thumbnailUrl = mainMedia?.thumbnail_url || syndicate.photo_url;
        } 
        // Otherwise, use the first photo's thumbnail if available
        else if (syndicate.syndicate_media?.[0]) {
          thumbnailUrl = syndicate.syndicate_media[0].thumbnail_url || syndicate.syndicate_media[0].original_url;
        }
        
        return {
          ...syndicate,
          syndicate_media: syndicate.syndicate_media || [], // Explicitly include media
          thumbnail_url: thumbnailUrl,
          unactivated_gift_credits: giftCreditsMap[syndicate.id] || 0,
        };
      });
      
      return syndicatesWithThumbnails as ActiveSyndicate[];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
